import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, Modal, Slider } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';

import defaultStoryImage from "@assets/images/story.png";
import CandleIcon from "@assets/icons/candle.svg";
import SnowIcon from "@assets/icons/snow.svg";
import FireIcon from "@assets/icons/fire.svg";
import { Repost } from '@organisms';
import { useAppDispatch, useAppSelector } from 'store';
import { axiosInstance } from 'utils';
import { Button, Checkbox, Input, Typography } from '@atoms';
import { setProfile } from 'store/slices/profileSlice';

import { Candle, Cryonized, Divider, StoryImg } from '../storyFace.styles';
import { getCandleByValue } from '../candles-icons';
import CandleListItem from "./candleListItem";
import CandleTimeLeft from "./candleTimeLeft";
import { useStoryDataContext } from "contexts/StoryContext";
import { ICandle } from "../storyFace.types";
import { ITributes } from "../../Tributes/tributes.types";

function StoryImage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { story, candles, addCandle, setTributes} = useStoryDataContext();
  const { t } = useTranslation();
  const { id } = useParams();
  const { profile } = useAppSelector((state) => state.profile);

  const [candleCountOpen, setCandleCountOpen] = useState(false);
  const [candleOpen, setCandleOpen] = useState<boolean>(false);

  const [candleEnergy, setCandleEnergy] = useState(1);
  const [candleMessage, setCandleMessage] = useState("");
  const [leaveTribute, setLeaveTribute] = useState(false);

  const toggleLeaveTribute = () => {
    setCandleEnergy(p => {
      if (p === Math.min(7, (profile?.energy_count || 0))) return p;
      return p + (leaveTribute ? -1 : 1);
    })
    setLeaveTribute(p => !p);
  };

  const handleSlider = (e: number) => {
    if (e === 0) return;
    if (leaveTribute && e < 2) return;
    setCandleEnergy(e);
  }

  const handleEnergyInput = (value: string) => {
    const num = Number(value);
    if (isNaN(num)) return;
    if (num <= 1) {
      setCandleEnergy(leaveTribute ? 2 : 1);
      return;
    }
    if (num > (profile?.energy_count ?? 7)) {
      setCandleEnergy(profile?.energy_count ?? 7);
      return;
    }
    setCandleEnergy(num);
  }

  const handleClickCandleClose = async () => {
    setCandleOpen(false);
    setCandleEnergy(1);
    setCandleMessage("");
    setLeaveTribute(false);
  };

  const handleClickCandle = async () => {
    try {
      const payload = {
        story: id,
        energy_amount: candleEnergy,
        ...(leaveTribute && {message: candleMessage})
      };

      const res = await axiosInstance.post<{candle:ICandle, tribute: ITributes}>(`candle-tribute/`, payload);
      if (res.data.tribute) setTributes((p) => [res.data.tribute, ...p])
      // firstData();
      if (profile) {
        dispatch(setProfile({...profile, energy_count: profile.energy_count - candleEnergy}));
      }
      addCandle(res.data.candle);
      toast.success(t("lightsOn"), { autoClose: 2000 });
    } catch (err) {
      // @ts-ignore
      const message = err?.response?.data?.message[0] as string;
      toast.error(message);
      console.error("Ошибка при загрузке данных:", err);
    } finally {
      setCandleOpen(false);
      setLeaveTribute(false)
      setCandleEnergy(1);
      setCandleMessage("");
    }
  };
  if (!story) return <></>;

  const handleCandleOpen = () => {
    if (profile) {
      if (!story.is_alive) setCandleOpen(true);
    } else {
      navigate("/login")
    }
  }

  return (
    <>
      <div className="story-current-image">
        <StoryImg
          src={story.image || defaultStoryImage}
          alt="avatar"
        />
      </div>
      {!story.is_alive && (
        <div className="light light-button" style={{cursor: story.is_alive ? 'not-allowed' : 'pointer'}}>
          <Typography
            style={{color: "#fff"}}
            onClick={handleCandleOpen}
          >
            {t("turnLight")}
          </Typography>
          <Modal
            forceRender
            open={candleOpen}
            onCancel={handleClickCandleClose}
            closeIcon={null}
            width={400}
            styles={{content: {background: "#F9F2EC"}, header: {background: "#F9F2EC"}}}
            title={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <p>{t("turnLight")}</p>
                <Candle>
                  <img src={CandleIcon} alt="candle"/>
                  <div>
                    {profile?.energy_count}
                    <span>/7</span>
                  </div>
                </Candle>
              </div>
            }
            footer={[
              <div key="btns">
                <Button variant="outlined" onClick={handleClickCandleClose} style={{fontSize: "14px"}}>
                  {t("close")}
                </Button>
                {
                  // @ts-ignore
                  profile?.energy_count > 0 && (
                    <Button onClick={handleClickCandle} style={{fontSize: "14px", marginLeft: 6}}
                            disabled={candleEnergy < (leaveTribute ? 2 : 1)}>
                      {t("publish")}
                    </Button>
                  )}
              </div>
            ]}
          >
            {candleOpen && (
              <Slider
                styles={{
                  root: {width: "100%"},
                  track: {
                    background: getGradient(candleEnergy, leaveTribute, 342, profile?.energy_count ?? 7),
                  }
                }}
                tooltip={{overlayClassName: "candle-slider"}}
                onChange={handleSlider}
                defaultValue={1}
                value={candleEnergy}
                disabled={!profile || profile.energy_count <= 0}
                max={profile?.energy_count ?? 7}
              />
            )}
            <Input
              style={{width: '80px', fontSize: "12px", marginBottom: "8px"}}
              height={'32px'}
              value={candleEnergy}
              onChange={handleEnergyInput}
            />
            {profile && profile.energy_count > 0 && story.enable_tribute ? (
              <div style={{display: 'flex', flexDirection: 'column', gap: '15px'}}>
                <Checkbox
                  label={t("leaveTribute")}
                  checked={leaveTribute}
                  // disabled={!candleEnergy}
                  onChange={toggleLeaveTribute}
                />
                {leaveTribute && (
                  <Input.TextArea
                    value={candleMessage}
                    onChange={setCandleMessage}
                    disabled={!leaveTribute}
                    maxLength={100}
                    showCount
                    placeholder={t("textTribute")}
                    style={{height: "130px", background: !leaveTribute ? "#F9F2EC" : "#FFFBF8"}}
                  />
                )}

                <Typography as="label" style={{color: "#594E47"}}>{t("leaveTributePrive")}</Typography>
              </div>
            ) : (
              <div style={{padding: "18px", borderRadius: "6px", border: "1px dashed #E4D7CD", background: "#FFFBF8"}}>
                {!story.enable_tribute ? (
                  <Typography as="h4">{t("authorForbade")}</Typography>
                ) : (
                  <Typography as="h4">{t("usedAllBalance")}</Typography>
                )}
              </div>
            )}
          </Modal>
        </div>
      )}

      {story.is_cryonized && (
        <Cryonized>
          <img src={SnowIcon} alt="snow"/>
          {t("cryonized")}
        </Cryonized>
      )}
      {(!story.is_alive && candles) && (
        <img
          src={getCandleByValue(candles.results)}
          alt="candle"
          style={{
            position: "absolute",
            bottom: "3px",
            right: "0",
          }}
        />
      )}
      {!story.is_alive && (
        <div className="light-peoples">
          {!!candles?.count ? (
            <>
              {candles?.results?.slice(-5).map((el, index) => (
                <Avatar
                  key={index}
                  size={32}
                  style={{
                    background: "#F9F2EC",
                    marginRight: "-18px",
                    cursor: "pointer",
                  }}
                  icon={
                    el.user.image ? (
                      <img src={el.user.image} alt="avatar"/>
                    ) : (
                      <UserOutlined/>
                    )
                  }
                  onClick={() => setCandleCountOpen(true)}
                />
              ))}

              {candles?.count > 5 && (
                <div
                  className="light-count"
                  onClick={() => setCandleCountOpen(true)}
                >
                  +{candles.count - 5}
                </div>
              )}
            </>
          ) : (
            <Avatar
              size={32}
              style={{
                marginRight: "-18px",
                cursor: "pointer",
              }}
              icon={<img src={FireIcon} alt="fire"/>}
              onClick={() => setCandleCountOpen(true)}
            />
          )}

          <Modal
            forceRender
            open={candleCountOpen}
            onCancel={() => setCandleCountOpen(false)}
            cancelText={t("close")}
            closeIcon={null}
            footer={[
              <Button
                variant="outlined"
                key="cancel"
                onClick={() => setCandleCountOpen(false)}
              >
                {t("close")}
              </Button>,
            ]}
            width="362px"
          >
            <div>
              {candles?.results && <CandleTimeLeft candleCount={candles.results}/>}
              {candles && candles?.count > 0 ? candles?.results.map((el) => (
                <CandleListItem el={el}/>
              )) : (
                <div className="zero-candle-repost">
                  <Divider style={{background: "#e4d7cd"}}/>
                  <Typography as="h2">{t("lovedRepost")}</Typography>
                  <Typography
                    style={{textAlign: "center", marginBottom: "30px"}}
                  >
                    {t("repost")}
                  </Typography>
                  <Repost name={story.name} isCandle/>
                </div>
              )}
            </div>
          </Modal>
        </div>
      )}

    </>
  )
}

const getGradient = (value: number, tributeEnabled: boolean, sliderWidth = 300, max: number) => {
  const stepWidth = sliderWidth / max; // Ширина одного шага
  const tributeWidth = tributeEnabled ? stepWidth : 0; // Добавляем 1 шаг для трибьюта
  const lightWidth = Math.max(0, value * stepWidth - tributeWidth); // Коррекция, чтобы темный цвет был четким

  return `linear-gradient(to right, #C6A691 ${lightWidth}px, #89796E ${lightWidth}px, #89796E ${lightWidth + tributeWidth}px)`;
};

export default StoryImage;
